/**
 * Shared export utilities matching Property Portfolio formatting.
 * Used by Revenue exports (Bookings vs INV, Invoices vs Payments)
 * and per-client statement exports.
 */
import { currencySymbol } from './chartFormatters';

// ── Constants ──

export const PDF_PRIMARY = [22, 101, 52];
export const PDF_SLATE = [51, 65, 85];
export const PDF_MUTED = [100, 116, 139];
export const PDF_MARGIN = 14;
export const EXCEL_CURRENCY_FORMAT = '#,##0.00';

// ── PDF Helpers ──

/** Build a currency formatter for PDF cell values. */
export function pdfCurrencyFormatter(dc) {
  const sym = currencySymbol(dc);
  return (v) => v != null
    ? `${v < 0 ? '-' : ''}${sym}${Math.abs(v).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
    : '';
}

/** Return formatted "Generated on" date string. */
export function generatedOnDate() {
  return new Date().toLocaleDateString('en-GB', { day: '2-digit', month: 'long', year: 'numeric' });
}

/**
 * Draw the branded header bar (Portfolio style).
 * Returns the Y position below the header for content placement.
 */
export function drawBrandedHeader(doc, { title, scopeLine, filtersLine }) {
  const PAGE_W = doc.internal.pageSize.getWidth();
  const generatedOn = generatedOnDate();

  doc.setFillColor(...PDF_PRIMARY);
  doc.rect(0, 0, PAGE_W, 28, 'F');
  doc.setTextColor(255, 255, 255);
  doc.setFontSize(14);
  doc.setFont('helvetica', 'bold');
  doc.text(title || 'Bonjour Residences', PDF_MARGIN, 10);
  doc.setFontSize(10);
  doc.setFont('helvetica', 'normal');
  doc.text(scopeLine || '', PDF_MARGIN, 17);
  if (filtersLine) {
    doc.setFontSize(8);
    doc.text(filtersLine, PDF_MARGIN, 23);
  }
  doc.setFontSize(8);
  doc.text(`Generated ${generatedOn}`, PAGE_W - PDF_MARGIN, 10, { align: 'right' });
  doc.setTextColor(0, 0, 0);

  return filtersLine ? 32 : 30;
}

/**
 * Draw branded footer on every page (Portfolio style).
 */
export function drawBrandedFooter(doc, { footerNote } = {}) {
  const PAGE_W = doc.internal.pageSize.getWidth();
  const generatedOn = generatedOnDate();
  const pageCount = doc.internal.getNumberOfPages();
  for (let i = 1; i <= pageCount; i++) {
    doc.setPage(i);
    doc.setFontSize(7);
    doc.setTextColor(...PDF_MUTED);
    doc.setFont('helvetica', 'italic');
    doc.text(footerNote || `Generated ${generatedOn}`, PDF_MARGIN, doc.internal.pageSize.getHeight() - 12);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8);
    doc.text(
      `Page ${i} of ${pageCount}`,
      PAGE_W / 2,
      doc.internal.pageSize.getHeight() - 6,
      { align: 'center' }
    );
  }
}

/** Default autoTable options matching Portfolio style. */
export const TABLE_DEFAULTS = {
  margin: { left: PDF_MARGIN, right: PDF_MARGIN },
  headStyles: { fillColor: PDF_SLATE, textColor: 255, fontStyle: 'bold', fontSize: 8 },
  bodyStyles: { fontSize: 8 },
  alternateRowStyles: { fillColor: [248, 250, 252] },
  theme: 'grid',
};

// ── Excel Helpers ──

/**
 * Create a formatted worksheet and append it to the workbook.
 * Mirrors PropertyPortfolioPage addSheet: auto-width columns, number formats.
 *
 * @param {object} wb   - XLSX workbook
 * @param {object} XLSX - XLSX module
 * @param {string} name - sheet name
 * @param {Array[]} aoa - array-of-arrays data (first row = headers)
 * @param {object} opts - { formats: { colIndex: formatString }, merges: [] }
 */
export function addFormattedSheet(wb, XLSX, name, aoa, opts = {}) {
  const ws = XLSX.utils.aoa_to_sheet(aoa);

  // Column auto-width (scan up to 40 rows)
  if (aoa.length > 0) {
    ws['!cols'] = aoa[0].map((h, ci) => {
      let max = String(h || '').length;
      for (let ri = 1; ri < Math.min(aoa.length, 40); ri++) {
        const cell = aoa[ri]?.[ci];
        if (cell != null) max = Math.max(max, String(cell).length);
      }
      return { wch: Math.max(max + 2, 10) };
    });
  }

  // Apply number formats to data rows
  if (opts.formats) {
    const range = XLSX.utils.decode_range(ws['!ref'] || 'A1');
    for (let ci = range.s.c; ci <= range.e.c; ci++) {
      const fmt = opts.formats[ci];
      if (!fmt) continue;
      for (let ri = range.s.r + 1; ri <= range.e.r; ri++) {
        const addr = XLSX.utils.encode_cell({ r: ri, c: ci });
        if (ws[addr] && typeof ws[addr].v === 'number') {
          ws[addr].z = fmt;
        }
      }
    }
  }

  if (opts.merges) ws['!merges'] = opts.merges;

  XLSX.utils.book_append_sheet(wb, ws, name);
}
